import { debounce } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PermissionsRequired } from '../../type/menu-type';
import { CheckPermissions } from '../../utils/CheckPermissions';
import CustomSelect from './CustomSelect';

interface OptionType {
    label: string;
    value: string;
}

interface Props {
    id: string;
    label: string | React.ReactNode;
    formik: any;
    minInputLength?: number;
    permissions?: PermissionsRequired;
    containerClassName?: string;
    isMulti?: boolean;
    isDisabled?: boolean;
    fetchOptions: (searchText: string | null) => Promise<OptionType[]>;
    error?: any;
    setValue?: (value: any) => void;
    required?: boolean;
    /** Valor inicial para mostrar cuando ya tenemos el dato (ej: al editar) - evita buscar por UUID */
    initialOption?: OptionType | null;
}

const CustomSelectApiHookForm: React.FC<Props> = ({
    id,
    label,
    formik,
    minInputLength = 2,
    permissions,
    containerClassName,
    isMulti = false,
    isDisabled = false,
    fetchOptions,
    error,
    setValue,
    required = false,
    initialOption = null,
}) => {

    const [options, setOptions] = useState<OptionType[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [filtersValue, setFiltersValue] = useState<any>([]);
    const [loading, setLoading] = useState(false);

    // Always call the latest fetchOptions (avoid stale closures inside debounced callbacks)
    const fetchOptionsRef = useRef(fetchOptions);
    useEffect(() => {
        fetchOptionsRef.current = fetchOptions;
    }, [fetchOptions]);

    // Debounced fetch function
    const debouncedFetch = useRef(
        debounce(async (value: string) => {
            try {
                const fetchedOptions = await fetchOptionsRef.current(value || null);
                setOptions(fetchedOptions);
            } catch (error) {
                console.error('Error fetching options:', error);
                setOptions([]);
            } finally {
                setLoading(false);
            }
        }, 600)
    ).current;

    // Fetch options immediately (no debounce) - used when menu opens
    const fetchImmediately = useCallback(async (searchText: string | null) => {
        setLoading(true);
        try {
            const fetchedOptions = await fetchOptions(searchText);
            setOptions(fetchedOptions);
        } catch (error) {
            console.error('Error fetching options:', error);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    }, [fetchOptions]);

    // When menu opens: if minInputLength allows empty search, fetch options so they appear without typing
    const handleMenuOpen = useCallback(() => {
        if (minInputLength === 0 && options.length === 0) {
            fetchImmediately(null);
        }
    }, [minInputLength, options.length, fetchImmediately]);

    // Handle input change with debounce
    const handleInputChange = useCallback((value: string) => {
        setInputValue(value);
        if (value.length >= minInputLength) {
            setLoading(true);
            debouncedFetch(value);
        } else {
            setOptions([]);
            setLoading(false);
        }
    }, [minInputLength]);

    // Handle selected values change
    const handleFiltersValue = useCallback((data: OptionType | OptionType[] | null) => {
        const object = isMulti
            ? (data as OptionType[])
            : (data as OptionType | null);
        const newValue = isMulti
            ? (object as OptionType[])?.map(item => item.value)
            : (object as OptionType | null)?.value;
        formik.setFieldValue(id, newValue);
        setValue && setValue({ id: newValue, name: (object as OptionType | null)?.label, object: object || null });
        setFiltersValue(data);
    }, [id]);

    // Function to get value by path from formik values
    const getValueByPath = (obj: any, path: string): any => {
        return path
            .replace(/\[(\w+)\]/g, '.$1') // convert [0] to .0
            .split('.') // divide string into parts: ['products', '0', 'id']
            .reduce((acc, part) => acc?.[part], obj);
    };

    // Fetch initial options if necessary (runs on mount and when formik value changes externally)
    useEffect(() => {
        const fieldValue = getValueByPath(formik.values, id);

        if (!fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0)) {
            setFiltersValue(isMulti ? [] : null);
            return;
        }

        const valueInOptions = isMulti
            ? options.filter((o) => Array.isArray(fieldValue) && fieldValue.includes(o.value))
            : options.find((o) => o.value === fieldValue);

        if (valueInOptions && (isMulti ? Array.isArray(valueInOptions) && valueInOptions.length > 0 : true)) {
            setFiltersValue(isMulti ? valueInOptions : valueInOptions);
            return;
        }

        setLoading(true);
        fetchOptions(null)
            .then((fetchedOptions: any) => {
                const filteredOptions = fetchedOptions.filter((option: any) =>
                    Array.isArray(fieldValue)
                        ? fieldValue.includes(option.value)
                        : option.value === fieldValue
                );
                setOptions((prev) => {
                    const toAdd = filteredOptions.filter((o: any) => !prev.some((p) => p.value === o.value));
                    return toAdd.length ? [...prev, ...toAdd] : prev;
                });
                setFiltersValue(isMulti ? filteredOptions : filteredOptions[0] ?? null);
            })
            .catch((error: any) => console.error('Error fetching options:', error))
            .finally(() => setLoading(false));
    }, [formik.values, id]);

    // Mostrar initialOption cuando tenemos el dato (ej: empresa padre al editar)
    useEffect(() => {
        const fieldValue = getValueByPath(formik.values, id);
        if (initialOption && fieldValue && initialOption.value === fieldValue) {
            setFiltersValue(isMulti ? [initialOption] : initialOption);
            setOptions(prev => prev.some(o => o.value === initialOption.value) ? prev : [...prev, initialOption]);
        }
    }, [initialOption, formik.values, id]);

    return (
        <>
            {((!permissions) || (permissions && CheckPermissions(permissions))) && (
                <CustomSelect
                    id={id}
                    label={label}
                    isSearchable
                    isClearable
                    isMulti={isMulti}
                    isDisabled={isDisabled}
                    options={options}
                    isLoading={loading}
                    onInputChange={handleInputChange}
                    onMenuOpen={handleMenuOpen}
                    onChange={handleFiltersValue}
                    value={filtersValue}
                    noOption={() => (inputValue?.length < minInputLength ? `Introduce al menos ${minInputLength} letras` : 'No se encontraron opciones')}
                    containerClassName={containerClassName}
                    error={error}
                    required={required}
                />
            )}
        </>
    );
};

export default CustomSelectApiHookForm;